import React from 'react';
import { createPortal } from 'react-dom';
import { Star, X, FileText, Image as ImageIcon, Mic } from 'lucide-react';

/**
 * "Starred Messages" page (WhatsApp-style) — lists every message the current
 * user starred, across all chats. Clicking an item navigates to its chat.
 * Empty state follows the app's icon + title + hint pattern.
 */
const StarredMessagesModal = ({ starredMessages, onClose, onOpenChat, onUnstar, c, t, isRTL, theme }) => {
  const sorted = [...(starredMessages || [])].sort((a, b) => (b.starredAt || 0) - (a.starredAt || 0));

  const typeIcon = (type) => {
    if (type === 'image') return <ImageIcon size={14} />;
    if (type === 'voice') return <Mic size={14} />;
    if (['pdf', 'excel', 'word', 'powerpoint'].includes(type)) return <FileText size={14} />;
    return null;
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: theme === 'dark' ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', backdropFilter: 'blur(4px)',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          background: theme === 'dark' ? '#1e293b' : '#ffffff',
          borderRadius: '24px', boxShadow: c.shadow, padding: '24px',
          height: '80vh', border: `1px solid ${c.border}`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: c.text, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Star size={20} color="#f59e0b" fill="#f59e0b" />
            {t.starredMessages}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex' }}
            aria-label={t.cancel}
          >
            <X size={20} />
          </button>
        </div>

        {sorted.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: c.textSecondary, padding: '20px' }}>
            <Star size={56} style={{ marginBottom: '16px', opacity: 0.4 }} />
            <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: c.text }}>
              {t.noStarredMessages}
            </p>
            <p style={{ fontSize: '14px', color: c.textSecondary, maxWidth: '320px', lineHeight: 1.7 }}>
              {t.starredHint}
            </p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sorted.map((msg) => (
              <div
                key={msg.id}
                className="employee-item-card"
                onClick={() => onOpenChat(msg)}
                style={{
                  display: 'flex', flexDirection: 'column', gap: '6px',
                  padding: '12px 14px', borderRadius: '14px', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#2e7cba', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {msg.sent ? t.you : (msg.senderName || msg.chatName)}
                    <span style={{ color: c.textSecondary, fontWeight: '500' }}> ← {msg.chatName}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', color: c.textSecondary, direction: 'ltr' }}>{msg.time}</span>
                    <button
                      type="button"
                      title={t.unstarAction}
                      onClick={(e) => { e.stopPropagation(); onUnstar(msg); }}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', padding: '2px' }}
                    >
                      <Star size={15} color="#f59e0b" fill="#f59e0b" />
                    </button>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: c.text }}>
                  {typeIcon(msg.type)}
                  <span dir="auto" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {msg.text || msg.mediaName || (msg.type === 'image' ? '📷' : msg.type === 'voice' ? '🎤' : '')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StarredMessagesModal;
